/**
 * Error Helper Utilities
 * Provides specific, user-friendly error messages based on error context
 */

/**
 * Get a specific error message based on error type and context
 * @param {Error|Object} error - The error object
 * @param {string} operation - What operation was being performed (e.g., "load equipment", "save changes")
 * @param {Object} context - Additional context for the error message
 * @returns {string} A user-friendly error message
 */
export const getSpecificErrorMessage = (error, operation, context = {}) => {
  // Handle API response errors
  if (error.response) {
    const status = error.response.status;
    const serverMessage = error.response.data?.error ||
                         error.response.data?.message ||
                         error.response.data?.details;

    switch (status) {
      case 400:
        return handleBadRequest(serverMessage, operation, context);
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return handleForbidden(serverMessage, operation, context);
      case 404:
        return handleNotFound(serverMessage, operation, context);
      case 409:
        return handleConflict(serverMessage, operation, context);
      case 422:
        return handleValidationError(serverMessage, operation, context);
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return `Server error while trying to ${operation}. Please try again or contact support if the problem persists.`;
      case 502:
      case 503:
      case 504:
        return `The server is temporarily unavailable. Please try again in a few moments.`;
      default:
        return serverMessage || `Unable to ${operation}. Please try again.`;
    }
  }

  // Handle network errors
  if (error.message?.includes('NetworkError') ||
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('ECONNREFUSED')) {
    return `Connection error while trying to ${operation}. Please check your internet connection and try again.`;
  }

  // Handle timeout errors
  if (error.message?.includes('timeout') || error.code === 'ECONNABORTED') {
    return `Request timed out while trying to ${operation}. The server may be busy. Please try again.`;
  }

  // Handle CSRF errors
  if (error.message?.includes('CSRF')) {
    return 'Your session security token has expired. Please refresh the page and try again.';
  }

  // Handle specific business logic errors
  if (context.resourceType) {
    return getResourceSpecificError(error, operation, context);
  }

  // Default fallback with original error message if available
  return error.message || `An unexpected error occurred while trying to ${operation}.`;
};

/**
 * Handle 400 Bad Request errors with specific messages
 */
const handleBadRequest = (serverMessage, operation, context) => {
  if (serverMessage) {
    // Check for common validation patterns
    if (serverMessage.includes('required')) {
      return `Missing required information. ${serverMessage}`;
    }
    if (serverMessage.includes('invalid')) {
      return `Invalid input provided. ${serverMessage}`;
    }
    if (serverMessage.includes('already exists')) {
      return serverMessage;
    }
    return serverMessage;
  }

  return `Invalid request while trying to ${operation}. Please check your input and try again.`;
};

/**
 * Handle 403 Forbidden errors with specific messages
 */
const handleForbidden = (serverMessage, operation, context) => {
  if (context.resourceType) {
    return `You don't have permission to ${operation} ${context.resourceType}.`;
  }

  if (serverMessage?.includes('role')) {
    return `Your user role doesn't allow you to ${operation}. Contact an administrator if you need access.`;
  }

  return serverMessage || `Access denied. You don't have permission to ${operation}.`;
};

/**
 * Handle 404 Not Found errors with specific messages
 */
const handleNotFound = (serverMessage, operation, context) => {
  if (context.resourceType && context.resourceId) {
    return `${context.resourceType} '${context.resourceId}' was not found. It may have been deleted or moved.`;
  }

  if (context.resourceType) {
    return `The ${context.resourceType.toLowerCase()} you're looking for was not found.`;
  }

  if (operation.includes('barcode')) {
    return 'No equipment found with this barcode. The item may not be registered in the system.';
  }

  return serverMessage || `The requested item was not found.`;
};

/**
 * Handle 409 Conflict errors with specific messages
 */
const handleConflict = (serverMessage, operation, context) => {
  if (serverMessage?.includes('duplicate')) {
    return `This item already exists. ${serverMessage}`;
  }

  if (serverMessage?.includes('in use')) {
    return `Cannot ${operation} because the item is currently in use. ${serverMessage}`;
  }

  if (context.resourceType === 'Equipment') {
    return `This equipment is already assigned or in use. Please check its current status.`;
  }

  return serverMessage || `Cannot ${operation} due to a conflict with existing data.`;
};

/**
 * Handle 422 Validation errors with specific messages
 */
const handleValidationError = (serverMessage, operation, context) => {
  if (serverMessage?.includes('date')) {
    return `Invalid date format. Please check your date entries and try again.`;
  }

  if (serverMessage?.includes('quantity')) {
    return `Invalid quantity. Please enter a valid number greater than zero.`;
  }

  if (serverMessage?.includes('email')) {
    return `Invalid email address format. Please check and try again.`;
  }

  return serverMessage || `Validation error while trying to ${operation}. Please check your input.`;
};

/**
 * Get resource-specific error messages
 */
const getResourceSpecificError = (error, operation, context) => {
  const { resourceType } = context;

  switch (resourceType) {
    case 'Equipment':
      return getEquipmentError(error, operation, context);
    case 'Job':
      return getJobError(error, operation, context);
    case 'User':
      return getUserError(error, operation, context);
    case 'Warehouse':
    case 'Rack':
    case 'Bay':
      return getLocationError(error, operation, context);
    default:
      return error.message || `Error with ${resourceType}: Unable to ${operation}.`;
  }
};

/**
 * Equipment-specific error messages
 */
const getEquipmentError = (error, operation, context) => {
  if (operation.includes('assign')) {
    return 'Unable to assign equipment. It may already be assigned to another job or unavailable.';
  }

  if (operation.includes('return')) {
    return 'Unable to return equipment. Please verify the equipment status and try again.';
  }

  if (operation.includes('transfer')) {
    return 'Unable to transfer equipment. Please check that both source and destination are valid.';
  }

  if (context.barcode) {
    return `No equipment found with barcode '${context.barcode}'. Please verify the barcode and try again.`;
  }

  return `Unable to ${operation} equipment. ${error.message || 'Please try again.'}`;
};

/**
 * Job-specific error messages
 */
const getJobError = (error, operation, context) => {
  if (operation.includes('create') && error.message?.includes('duplicate')) {
    return 'A job with this number already exists. Please use a different job number.';
  }

  if (operation.includes('delete')) {
    return 'Cannot delete this job. It may have equipment assigned or be in active use.';
  }

  if (context.jobId) {
    return `Unable to ${operation} job '${context.jobId}'. ${error.message || ''}`;
  }

  return `Unable to ${operation} job. ${error.message || 'Please try again.'}`;
};

/**
 * User-specific error messages
 */
const getUserError = (error, operation, context) => {
  if (operation.includes('login')) {
    if (error.message?.includes('credentials')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (error.message?.includes('locked')) {
      return 'Your account has been locked. Please contact an administrator.';
    }
    return 'Unable to log in. Please check your credentials and try again.';
  }

  if (operation.includes('role')) {
    return 'Unable to update user role. You may not have permission to modify user roles.';
  }

  return `Unable to ${operation} user. ${error.message || 'Please try again.'}`;
};

/**
 * Location-specific error messages (Warehouse, Rack, Bay)
 */
const getLocationError = (error, operation, context) => {
  const locationType = context.resourceType.toLowerCase();

  if (operation.includes('delete')) {
    if (error.message?.includes('items')) {
      return `Cannot delete this ${locationType}. It contains equipment that must be moved first.`;
    }
    return `Cannot delete this ${locationType}. It may be in use.`;
  }

  if (operation.includes('create') && error.message?.includes('exists')) {
    return `A ${locationType} with this name already exists. Please use a different name.`;
  }

  return `Unable to ${operation} ${locationType}. ${error.message || 'Please try again.'}`;
};

/**
 * Format error for display with additional context
 */
export const formatErrorForDisplay = (error, operation, context = {}) => {
  const message = getSpecificErrorMessage(error, operation, context);

  // Add helpful context based on error type
  const suggestions = getErrorSuggestions(error, operation, context);

  return {
    message,
    suggestions,
    isRetryable: isRetryableError(error),
    requiresAuth: error.response?.status === 401,
    requiresRefresh: error.message?.includes('CSRF') || error.message?.includes('ChunkLoadError')
  };
};

/**
 * Get suggestions for resolving the error
 */
const getErrorSuggestions = (error, operation, context) => {
  const suggestions = [];

  if (error.response?.status === 401) {
    suggestions.push('Log in again to continue');
  }

  if (error.response?.status === 403) {
    suggestions.push('Contact an administrator if you need access');
  }

  if (error.response?.status >= 500) {
    suggestions.push('Try again in a few moments');
    suggestions.push('Contact support if the problem persists');
  }

  if (error.message?.includes('Network') || error.message?.includes('fetch')) {
    suggestions.push('Check your internet connection');
    suggestions.push('Try refreshing the page');
  }

  if (error.message?.includes('timeout')) {
    suggestions.push('The server may be busy, please wait and try again');
  }

  return suggestions;
};

/**
 * Check if an error is retryable
 */
const isRetryableError = (error) => {
  if (!error.response) return true; // Network errors are retryable

  const status = error.response.status;
  const retryableStatuses = [408, 429, 500, 502, 503, 504];

  return retryableStatuses.includes(status);
};

/**
 * Create a user-friendly error notification object
 */
export const createErrorNotification = (error, operation, context = {}) => {
  const formatted = formatErrorForDisplay(error, operation, context);

  return {
    message: formatted.message,
    variant: formatted.requiresAuth ? 'warning' : 'error',
    persist: formatted.requiresAuth || formatted.requiresRefresh,
    action: formatted.requiresRefresh ? {
      label: 'Refresh',
      onClick: () => window.location.reload()
    } : formatted.isRetryable ? {
      label: 'Retry',
      onClick: context.retryCallback
    } : null
  };
};

export default {
  getSpecificErrorMessage,
  formatErrorForDisplay,
  createErrorNotification
};